/* eslint-disable @typescript-eslint/no-use-before-define */

import { type CommonProps,useObservability, useObservabilityManager } from "@qamf/observability";
import { loadStripe } from "@stripe/stripe-js";

import { PaymentsAppInitQueryParam, PaymentsAppReadyEvent } from "./interfaces/environment";
import { type CommandMessage, type IPaymentCurrency, type IPriceFormatResult, type IProviderData, type ResultPayload, type Settings } from "./interfaces/interfaces";
import { ERROR_TYPE, ERRORS_TO_HANDLE, GENERIC_ERROR_TYPE, OPERATION_ID, PAYMENT_FORM_ID, STRIPE_ERROR, STRIPE_PAYMENT_ERROR } from "./interfaces/labels";
import type { StripePaymentError } from "./interfaces/payment-error";
import { AppInsightsEvent } from "./observability-events";



async function settingsResolver() {
	const contextFileUrl = __LAUNCH_MODE__ === "Standard"
		? "/context.json"
		: `/settings/${__ENVIRONMENT__}-${__CHANNEL__}.json`;
	const response = await fetch(contextFileUrl, { method: "GET", cache: "no-cache" }).then((data) => data.json());
	return response as Settings;
}

function priceFormatter(amount: number, options: IPaymentCurrency): IPriceFormatResult {
	const decimalSep = options.DecimalSep ?? ".";
	const decimalsDigitsOpt = options.DecDigits ? Math.abs(options.DecDigits) : 0;
	const decimalsDigits = isNaN(decimalsDigitsOpt) ? 0 : decimalsDigitsOpt;
	const thousandsSep = options.ThousandSep ?? "";
	const sign = amount < 0 ? "-" : "";
	const amountParsed = Math.abs(Number(amount) || 0).toFixed(decimalsDigits);
	const integerPart = parseInt(amountParsed);
	const integerPartString = integerPart.toString();
	const hasThousands = integerPartString.length > 3 ? integerPartString.length % 3 : 0;
	const hasDecimals = decimalsDigits > 0;
	const composition = {
		symbolBefore:
			options?.Symbol && options.SymbolBeforeValue ? options.Symbol : "",
		sign,
		thousands: hasThousands ? integerPartString.substr(0, hasThousands) : "",
		thousandsSeparator: hasThousands ? thousandsSep : "",
		integers: integerPartString
			.substr(hasThousands)
			.replace(/(\d{3})(?=\d)/g, "$1" + thousandsSep),
		decimals: hasDecimals
			? Math.abs(amount - integerPart)
				.toFixed(decimalsDigits)
				.split(".")[1]
			: "",
		decimalsSeparator: hasDecimals ? decimalSep : "",
		symbolAfter:
			options?.Symbol && !options.SymbolBeforeValue ? options.Symbol : ""
	};
	const result = (composition.symbolBefore ? composition.symbolBefore + " " : "")
		+ composition.sign
		+ (composition.thousands ? composition.thousands + composition.thousandsSeparator : "")
		+ composition.integers
		+ composition.decimalsSeparator
		+ composition.decimals
		+ (composition.symbolAfter ? " " + composition.symbolAfter : "");
	return { composition, result };
}


function redirectWithParams(code: string, message: string, operationId?: string) {
	const Url = new URL(window.location.href);

	const params = new URLSearchParams();
	params.set(code, message);
	if (operationId)
		params.set("operationId", operationId);
	Url.search = params.toString();
	location.href = Url.toString();
}

export function showOverlay() {
	const loadingContent = document.getElementsByClassName("payment")[0];
	const formPayment = document.getElementById(PAYMENT_FORM_ID);
	if (loadingContent instanceof HTMLElement && formPayment) {
		loadingContent.style.display = "block";
		formPayment.style.display = "none";
	}
}
export function hideOverlay() {
	const loadingContent = document.getElementsByClassName("payment")[0];
	const formPayment = document.getElementById(PAYMENT_FORM_ID);
	if (loadingContent instanceof HTMLElement && formPayment) {
		loadingContent.style.display = "none";
		formPayment.style.display = "flex";
	}
}


function onClosePopup() {
	window.close();
}

export async function stripeForm(paymentData: Extract<CommandMessage, { command: "stripe-payment" }>["payload"], targetElementId: string): Promise<void | StripePaymentError> {
	const contentHTML = `<form id=${PAYMENT_FORM_ID}>
						<div class="form-group">
							<div id="form-header">
								<img src="images/stripe_logo.png" alt="StripeLogo" height="90">
							</div>
							<div id="stripe-div"></div>
							<button type="submit" id="payment-button">
								<div
									id="spinner"
									class="spinner hidden"
								></div>
								<span id="button-text">PAY</span>
							</button>
							<div
								id="error-message"
							></div>
							<div
							<div id="close-button-container">
								<button id="close-button">Cancel Payment</button>
							</div>
						</div>
					</form>
					<div class="splash-screen payment" style="display: none">
						<span class="splash-content">
							<div class="splash-spinner">
								<div class="bounce1"></div>
								<div class="bounce2"></div>
								<div class="bounce3"></div>
							</div>
						</span>
					</div>`;
	document.body.innerHTML = contentHTML;

	document.getElementById("close-button")?.addEventListener("click", onClosePopup);

	const stripe = await loadStripe(paymentData.data.PublicKey, { stripeAccount: paymentData.data.StripeUserId });
	if (!stripe)
		return "load-sdk";

	const { result } = priceFormatter(paymentData.amount, paymentData.currency);
	const button = document.getElementById("button-text");

	if (!button)
		return "button-pay-error";

	button.innerText = `PAY ${result}`;

	const elements = stripe.elements({
		clientSecret: paymentData.data.ClientSecret,
		appearance: {
			theme: "stripe",
			variables: {
				colorPrimary: "#47a2a6",
				focusBoxShadow: "#47a2a6",
				iconColor: "#47a2a6",
				tabIconSelectedColor: "#47a2a6"
			}
		}
	});

	if (!elements)
		return "load-elements";

	const paymentElement = elements.create("payment");

	paymentElement.on("loaderror", function(event) {
		redirectWithParams(STRIPE_ERROR, "create-elements", paymentData.operationId);
	});

	paymentElement.mount(`#${targetElementId}`);

	const form = document.getElementById(PAYMENT_FORM_ID);
	if (!form)
		return "form-undefined";

	//FIXME: [lib-payments] Implement Dynamic Language Switching in the Stripe Form: #68470

	form.addEventListener("submit", async(event) => {
		event.preventDefault();
		const {trackEvent} = useObservability();

		trackEvent(AppInsightsEvent.SubmittedStripeForm, {
			timestamp: new Date().toISOString(),
			operationId: paymentData.operationId
		});


		showOverlay();

		await elements.submit();

		const returnUrl = window.location.href.split("?")[0];
		const { error: confirmError } = await stripe!.confirmPayment({
			elements,
			confirmParams: {
				return_url: returnUrl,
			},
			clientSecret: paymentData.data.ClientSecret,
		});

		if (confirmError) {
			trackEvent(AppInsightsEvent.SubmittedStripeFormFailed, {
				timestamp: new Date().toISOString(),
				errorMessage: confirmError.message ?? "No error message",
				operationId: paymentData.operationId
			});

			hideOverlay();
			const messageContainer = document.querySelector("#error-message");
			if (messageContainer instanceof HTMLElement) {
				messageContainer.textContent = "";
				if (confirmError.code && ERRORS_TO_HANDLE.includes(confirmError.code)) {
					messageContainer.textContent = confirmError.message ?? "";
					messageContainer.style.color = "red";
				}
			}
		}

	});
}

function redirectForm(
	url: string,
	method: "GET" | "POST" | "PUT" | "PATCH" | "get" | "post" | "put" | "patch" = "POST",
	data?: object
) {
	if (method.toLowerCase() === "get" && !data)
		return location.replace(url);

	const form = document.createElement("form");
	form.style.display = "none";
	form.setAttribute("action", url);
	form.setAttribute("method", method.toLowerCase());
	let inputs = "";
	if (data) {
		Object.keys(data).forEach(field => {
			const value = (data as any)[field];
			inputs += `<input type="hidden" name="${field}" value="${value}" />`;
		});
		form.innerHTML = inputs;
	}
	document.body.appendChild(form);
	form.submit();
}

function setupSquareSdk(paymentData: Extract<CommandMessage, { command: "square-payment" }>["payload"]) {
	const appId = paymentData.data.AppId;
	const locationId = paymentData.data.LocationId;
	const amount = paymentData.amount;
	const operationId = paymentData.operationId;
	const systemId = paymentData.data.SystemId;
	const parentUrl = paymentData.parentUrl;
	console.debug("Initializing Square Payment Form with data: ", {
		appId,
		locationId,
		amount,
		operationId,
		systemId,
		parentUrl,
	});

	async function initializeCard(payments: any) {
		const card = await payments.card();
		console.log("Attaching Card payment method to the DOM", card);
		await card.attach("#card-container");

		return card;
	}

	function buildPaymentRequest(payments: any) {
		return payments.paymentRequest({
			countryCode: "US",
			currencyCode: "USD",
			total: {
				amount: String(amount),
				label: "Total",
			},
		});
	}

	async function initializeApplePay(payments: any) {
		const paymentRequest = buildPaymentRequest(payments);
		console.log("Checking Apple Pay support with payment request: ", paymentRequest);
		const applePay = await payments.applePay(paymentRequest);
		console.log("Apple Pay payment method initialized: ", applePay);
		// Note: You do not need to `attach` applePay.
		return applePay;
	}

	async function createPayment(token: any) {
		const body = JSON.stringify({
			OperationId: operationId,
			Token: token,
		});

		const paymentResponse = await fetch(`${parentUrl}/api/centers/${systemId}/payment/token`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body,
		});

		console.log("Payment response received: ", paymentResponse);

		if (paymentResponse.ok)
			return paymentResponse.json();


		const errorBody = await paymentResponse.text();
		console.error("Payment failed: ", errorBody);
		throw new Error(errorBody);
	}

	// Change the parameter to 'paymentMethod'
	async function tokenize(paymentMethod: any) {
		const tokenResult = await paymentMethod.tokenize();
		console.log("Tokenization result: ", tokenResult);
		if (tokenResult.status === "OK")
			return tokenResult.token;
		else {
			let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
			if (tokenResult.errors) {
				errorMessage += ` and errors: ${JSON.stringify(
					tokenResult.errors,
				)}`;
			}

			throw new Error(errorMessage);
		}
	}

	// status is either SUCCESS or FAILURE;
	function displayPaymentResults(status: any) {
		const statusContainer = document.getElementById(
			"payment-status-container",
		);
		if (status === "SUCCESS") {
			statusContainer!.classList.remove("is-failure");
			statusContainer!.classList.add("is-success");
		} else {
			statusContainer!.classList.remove("is-success");
			statusContainer!.classList.add("is-failure");
		}

		statusContainer!.style.visibility = "visible";
	}

	async function buildSquareForm() {
		const thisWindow = window as any;
		if (!thisWindow.Square) {
			console.error("Square.js failed to load properly");
			throw new Error("Square.js failed to load properly");
		}


		let payments;
		try {
			payments = thisWindow.Square.payments(appId, locationId);
		} catch {
			const statusContainer = document.getElementById(
				"payment-status-container",
			);
			statusContainer!.className = "missing-credentials";
			statusContainer!.style.visibility = "visible";
			return;
		}

		let card;
		try {
			card = await initializeCard(payments);
		} catch (e) {
			console.error("Initializing Card failed", e);
			return;
		}

		let applePay;
		try {
			applePay = await initializeApplePay(payments);
		} catch (e) {
			console.error("Initializing Apple Pay failed", e);
			// There are a number of reason why Apple Pay may not be supported
			// (e.g. Browser Support, Device Support, Account). Therefore you should handle
			// initialization failures, while still loading other applicable payment methods.
		}

		async function handlePaymentMethodSubmission(event: any, paymentMethod: any) {
			event.preventDefault();

			try {
				// disable the submit button as we await tokenization and make a payment request.
				cardButton!.disabled = true;
				const token = await tokenize(paymentMethod);
				console.log("Token received:", token);
				const paymentResults = await createPayment(token);
				console.log("Payment Results:", paymentResults);
				displayPaymentResults("SUCCESS");

				console.debug("Payment Success", paymentResults);
			} catch (e) {
				cardButton.disabled = false;
				displayPaymentResults("FAILURE");
				console.error((e as any).message);
			}
		}

		const cardButton = document.getElementById("card-button") as HTMLButtonElement;
		cardButton.addEventListener("click", async function(event) {
			await handlePaymentMethodSubmission(event, card);
		});

		// Checkpoint 2.
		if (applePay) {
			const applePayButton = document.getElementById("apple-pay-button") as HTMLButtonElement;
			applePayButton.addEventListener("click", async function(event) {
				event.preventDefault();
				await handlePaymentMethodSubmission(event, applePay);
			});
		}
	};

	return buildSquareForm();
}

window.addEventListener("message", async(e) => {
	console.log("Message received in Payment App:", e.data);
 	const messageData = e.data as CommandMessage;
 	if (messageData?.payload?.operationId)
 		sessionStorage.setItem(OPERATION_ID, messageData.payload.operationId);

 	const {trackEvent} = useObservability();

 	if (messageData.command === "redirect") {

 		trackEvent(AppInsightsEvent.RedirectEvent, {
 			payload: messageData.payload,
 			operationId: messageData.payload.operationId
 		});

 		const { url, method, data } = (e.data as any).payload;
 		redirectForm(url, method, data);
 	} else if (messageData.command === "stripe-payment") {

 		trackEvent(AppInsightsEvent.StripeEvent,{
 			payload: messageData.payload,
 			operationId: messageData.payload.operationId
 		});


 		const formResult = await stripeForm(messageData.payload, "stripe-div");
 		if (formResult)
 			redirectWithParams(STRIPE_ERROR, formResult, messageData.payload.operationId);
	} else if (messageData.command === "square-payment")
		setupSquareSdk(messageData.payload);

});

window.addEventListener("load", async() => {
	const settings = await settingsResolver();
	if (!settings.appInsightsConnString) return;

	const { setupAppInsights, setCommonPropsResolver, setEventsPrefix } = useObservabilityManager();

	setupAppInsights({
		config: {
			connectionString: settings.appInsightsConnString,
			disableExceptionTracking: true,
			disableTelemetry: __LAUNCH_MODE__ === "DevelopSession"
		}
	});

	const rawEnvironment = settings.environment ?? __ENVIRONMENT__;
	const rawChannel = settings.channel ?? __CHANNEL__;

	const map = <T extends string, U extends string>(
		v: T,
		m: Record<T, U>
	): U => m[v] ?? (v as unknown as U);

	const Environment = map(rawEnvironment, {
		production: "Production",
		staging: "Staging",
		development: "Development",
		expo: "Expo"
	});

	const Channel = map(rawChannel, {
		stable: "Stable",
		beta:   "Beta",
	});

	setCommonPropsResolver((): CommonProps => ({
		Environment,
		Channel,
		Version: __VERSION__,
		Source: "PaymentApp",
		CenterId:  null,
		CompanyId: null,
		LaunchMode: __LAUNCH_MODE__
	}));

	setEventsPrefix("qubicaamf.payment_app");

	const {trackEvent} = useObservability();

	const isInit = location.href.indexOf(`?${PaymentsAppInitQueryParam}=`) > 0;
	console.log("Payment App isInit:", isInit);
	if (isInit) {
		if (window.opener)
			window.opener.postMessage(PaymentsAppReadyEvent, "*");
		else
			window.parent?.postMessage(PaymentsAppReadyEvent, "*");

		trackEvent(AppInsightsEvent.ReadyEvent, { isPopup: Boolean(window.opener) });

	} else {
 		const params = new URLSearchParams(window.location.search);
 		const parsedParams = Object.fromEntries(params.entries());

 		const operationId = params.get("operationId")
             ?? sessionStorage.getItem(OPERATION_ID)
 			?? undefined;

 		let providerData: IProviderData;
 		if (STRIPE_ERROR in parsedParams) {
 			providerData = {
 				name: ERROR_TYPE,
 				code: parsedParams[STRIPE_ERROR],
 				message: "Error occurred while initializing Stripe SDK"
 			};
 		} else if (STRIPE_PAYMENT_ERROR in parsedParams && parsedParams[STRIPE_PAYMENT_ERROR] === "failed") {
 			providerData = {
 				name: GENERIC_ERROR_TYPE,
 				code: parsedParams[STRIPE_PAYMENT_ERROR],
 				message: "Error during the payment"
 			};
 		} else
 			providerData = parsedParams;

 		const result: ResultPayload = {
 			providerReplied: true,
 			providerData
 		};

 		// if (window.opener)
 		// 	window.opener.postMessage(result, "*");
 		// else
 		// 	window.parent?.postMessage(result, "*");

		console.log("Posting result to parent window:", result);

 		trackEvent(AppInsightsEvent.ResultEvent, {
 			result: result,
 			operationId: operationId
 		});

 		sessionStorage.removeItem(OPERATION_ID);
 	}
});
